import { useState, useEffect } from 'react';

// SSR-safe check for window
const isClient = typeof window === 'object';

// Define the structure for stored data with expiration
interface StoredData<T> {
  value: T;
  expiry?: number; // Unix timestamp in milliseconds
}

export function useLocalStorage<T>(
  key: string,
  initialValue: T | (() => T),
  options?: { enabled?: boolean; expiresInMs?: number }
): [T, React.Dispatch<React.SetStateAction<T>>] {
  const storageKey = key;
  const enabled = options?.enabled ?? true;
  const expiresInMs = options?.expiresInMs;

  const getInitialValue = () =>
    initialValue instanceof Function ? initialValue() : initialValue;

  // Function to read from localStorage, handling expiration
  const readValue = (): T => {
    if (!isClient || !enabled) {
      return getInitialValue();
    }
    try {
      const item = window.localStorage.getItem(storageKey);
      if (!item) {
        return getInitialValue();
      }

      const parsedItem: StoredData<T> | T = JSON.parse(item);

      // Check if the stored item is in the new format with potential expiry
      if (typeof parsedItem === 'object' && parsedItem !== null && 'value' in parsedItem) {
        const storedData = parsedItem as StoredData<T>;
        // Check for expiration if expiry is present
        if (storedData.expiry !== undefined && Date.now() > storedData.expiry) {
          // Item has expired, remove it and return initial value
          window.localStorage.removeItem(storageKey);
          console.log(`localStorage key "${storageKey}" expired and removed.`);
          return getInitialValue();
        }
        // Item is not expired or has no expiry, return its value
        return storedData.value;
      } else {
        // Item is in the old format (just the value), return it directly
        // Note: This old format won't expire unless explicitly handled or overwritten
        return parsedItem as T;
      }
    } catch (error) {
      console.error(`Error reading localStorage key "${storageKey}":`, error);
      // If error parsing, assume invalid data and return initial value
      window.localStorage.removeItem(storageKey); // Clean up potentially corrupt data
      return getInitialValue();
    }
  };

  // State initialization: read from localStorage using the readValue function
  const [storedValue, setStoredValue] = useState<T>(readValue);

  // Effect to re-read when enabled or key changes (and on mount)
  useEffect(() => {
    // Re-read the value from storage when enabled state or key changes
    // This handles cases where enabled becomes true or the key changes dynamically
    setStoredValue(readValue());
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, storageKey]); // Depend on enabled and key

  // Effect to sync state to localStorage when storedValue, key, enabled, or expiresInMs changes
  useEffect(() => {
    if (!isClient || !enabled) {
      // If disabled or not client, don't write
      return;
    }

    try {
      if (storedValue === undefined || storedValue === null) {
        // Remove item if value is null or undefined
        window.localStorage.removeItem(storageKey);
      } else {
        let dataToStore: StoredData<T> | T = storedValue;

        if (expiresInMs !== undefined && expiresInMs > 0) {
          // If expiration is set, store value with expiry timestamp
          const expiry = Date.now() + expiresInMs;
          dataToStore = { value: storedValue, expiry: expiry };
        }
        // If expiresInMs is not set or <= 0, store just the value (old format)

        window.localStorage.setItem(storageKey, JSON.stringify(dataToStore));
      }
    } catch (error) {
      console.error(`Error setting localStorage key "${storageKey}":`, error);
    }
  }, [storageKey, storedValue, enabled, expiresInMs]); // Depend on all relevant values for writing

  return [storedValue, setStoredValue];
}